const SUBJECTBUDDY_HOSTNAME_PATTERN = /(^|\.)subjectbuddy\.com$/i;

export const ASSIGNMENT_HELP_INDEX_PATH = "/assignment-help";
export const BLOGS_INDEX_PATH = "/blogs";

function trimSlashes(value: string) {
  return value.replace(/^\/+|\/+$/g, "");
}

function buildDirectPath(slug?: string) {
  const normalizedSlug = trimSlashes(slug || "");

  if (!normalizedSlug) {
    return "/";
  }

  return `/${normalizedSlug}/`;
}

function normalizeInternalPathname(pathname: string) {
  const normalizedPathname = pathname || "/";
  const cleanPathname =
    normalizedPathname === "/" ? "/" : `/${trimSlashes(normalizedPathname)}`;

  if (/^\/assignment-help\/?$/i.test(cleanPathname)) {
    return ASSIGNMENT_HELP_INDEX_PATH;
  }

  if (/^\/blogs?\/?$/i.test(cleanPathname)) {
    return BLOGS_INDEX_PATH;
  }

  const assignmentMatch = cleanPathname.match(/^\/assignment-help\/([^/]+)\/?$/i);

  if (assignmentMatch) {
    return getAssignmentPagePath(assignmentMatch[1]);
  }

  const blogMatch = cleanPathname.match(/^\/blogs?\/([^/]+)\/?$/i);

  if (blogMatch) {
    return getBlogPath(blogMatch[1]);
  }

  return normalizedPathname;
}

export function getAssignmentPagePath(slug?: string) {
  return buildDirectPath(slug);
}

export function getBlogPath(slug?: string) {
  return buildDirectPath(slug);
}

export function getLegacyAssignmentPagePath(slug?: string) {
  const normalizedSlug = trimSlashes(slug || "");
  return normalizedSlug
    ? `${ASSIGNMENT_HELP_INDEX_PATH}/${normalizedSlug}/`
    : `${ASSIGNMENT_HELP_INDEX_PATH}/`;
}

export function getLegacyBlogPath(slug?: string) {
  const normalizedSlug = trimSlashes(slug || "");
  return normalizedSlug ? `${BLOGS_INDEX_PATH}/${normalizedSlug}/` : BLOGS_INDEX_PATH;
}

export function getLegacySingularBlogPath(slug?: string) {
  const normalizedSlug = trimSlashes(slug || "");
  return normalizedSlug ? `/blog/${normalizedSlug}/` : "/blog";
}

export function getSlugFromPathname(pathname: string) {
  const cleanPath = trimSlashes((pathname || "").split(/[?#]/)[0] || "");

  if (!cleanPath) {
    return undefined;
  }

  const segments = cleanPath.split("/").filter(Boolean);
  return segments[segments.length - 1];
}

export function normalizeInternalHref(href: string) {
  if (!href) {
    return href;
  }

  if (
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:")
  ) {
    return href;
  }

  try {
    const parsedUrl = new URL(href, "https://www.subjectbuddy.com");
    const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:/i.test(href);

    if (isAbsoluteUrl && !SUBJECTBUDDY_HOSTNAME_PATTERN.test(parsedUrl.hostname)) {
      return href;
    }

    const normalizedPathname = normalizeInternalPathname(parsedUrl.pathname);

    if (normalizedPathname === parsedUrl.pathname && !isAbsoluteUrl) {
      return href;
    }

    const normalizedHref = `${normalizedPathname}${parsedUrl.search}${parsedUrl.hash}`;

    if (isAbsoluteUrl) {
      return normalizedHref;
    }

    return normalizedHref;
  } catch {
    return href;
  }
}

export function normalizeInternalHtmlLinks(html: string) {
  if (!html) {
    return html;
  }

  return html.replace(/href=(['"])(.*?)\1/gi, (_match, quote, href) => {
    const normalizedHref = normalizeInternalHref(href);
    return `href=${quote}${normalizedHref}${quote}`;
  });
}
